// src/pages/dashboard/DashboardPage.jsx
import { useState, useEffect } from "react";
import { useAuth } from "../../hooks/useAuth";
import { getLocalJobs, getApprovedJobCount } from "../../services/firestoreService";
import { useNavigate } from "react-router-dom";
import AppLayout from "../../components/layout/AppLayout";
import styles from "./DashboardPage.module.css";
import {
  Briefcase, Zap, CreditCard, Target,
  Search, Laptop, Building2,
} from "lucide-react";

function getProfileScore(profile) {
  if (!profile) return 0;
  const checks = [
    !!profile.displayName,
    !!profile.bio,
    !!profile.location,
    profile.skills?.length > 0,
    profile.skills?.length >= 3,
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export default function DashboardPage() {
  const { userProfile, userRole, credits } = useAuth();
  const navigate = useNavigate();
  const [recentJobs,  setRecentJobs]  = useState([]);
  const [jobCount,    setJobCount]    = useState(null);
  const [loadingJobs, setLoadingJobs] = useState(true);

  const profileScore = getProfileScore(userProfile);

  useEffect(() => {
    async function loadStats() {
      try {
        const [all, count] = await Promise.all([
          getLocalJobs({ approved: true, maxLimit: 5 }),
          getApprovedJobCount(),
        ]);
        setRecentJobs(all);
        setJobCount(count);
      } catch {
        setJobCount(0);
      } finally {
        setLoadingJobs(false);
      }
    }
    loadStats();
  }, []);

  const incomplete = [
    !userProfile?.bio      && { label: "Add a bio",        action: () => navigate("/profile") },
    !userProfile?.location && { label: "Set your location", action: () => navigate("/profile") },
    !(userProfile?.skills?.length) && { label: "Add your skills", action: () => navigate("/profile") },
  ].filter(Boolean);

  return (
    <AppLayout>
      <div className={styles.page}>
        {/* ── Topbar ── */}
        <header className={styles.topbar}>
          <div>
            <h1 className={styles.greeting}>
              {getGreeting()}, {userProfile?.displayName?.split(" ")[0] || "there"} 👋
            </h1>
            <p className={styles.greetingSub}>
              {new Date().toLocaleDateString("en-ZA", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </p>
          </div>
          <div className={styles.topbarRight}>
            <span className={styles.roleBadge}>{userRole}</span>
          </div>
        </header>

        <main className={styles.main}>
          {/* ── Profile completion nudge ── */}
          {profileScore < 100 && (
            <div className={styles.completionCard}>
              <div className={styles.completionLeft}>
                <div className={styles.completionTitle}>Complete your profile</div>
                <div className={styles.completionSub}>A complete profile gets you better job matches.</div>
                <div className={styles.completionSteps}>
                  {incomplete.map((item, i) => (
                    <button key={i} className={styles.completionStep} onClick={item.action}>
                      <span className={styles.completionDot} />
                      {item.label} →
                    </button>
                  ))}
                </div>
              </div>
              <div className={styles.completionRight}>
                <div className={styles.progressRing}>
                  <svg viewBox="0 0 72 72" width="72" height="72">
                    <circle cx="36" cy="36" r="30" fill="none" stroke="var(--color-border)" strokeWidth="6" />
                    <circle cx="36" cy="36" r="30" fill="none"
                      stroke="var(--color-accent)" strokeWidth="6"
                      strokeDasharray={`${(profileScore / 100) * 188.5} 188.5`}
                      strokeLinecap="round" transform="rotate(-90 36 36)" />
                  </svg>
                  <span className={styles.progressPct}>{profileScore}%</span>
                </div>
              </div>
            </div>
          )}

          {/* ── Stat cards ── */}
          <div className={styles.statsRow}>
            <div className={styles.statCard} onClick={() => navigate("/jobs")} style={{ cursor: "pointer" }}>
              <div className={styles.statIcon}><Briefcase size={22} strokeWidth={2} /></div>
              <div className={styles.statBody}>
                <div className={styles.statValue}>
                  {loadingJobs ? "—" : (jobCount == null ? "—" : jobCount.toLocaleString())}
                </div>
                <div className={styles.statLabel}>Local gigs available</div>
              </div>
              <div className={styles.statArrow}>→</div>
            </div>

            <div className={styles.statCard} onClick={() => navigate("/jobs?mode=freelance")} style={{ cursor: "pointer" }}>
              <div className={styles.statIcon}><Zap size={22} strokeWidth={2} /></div>
              <div className={styles.statBody}>
                <div className={styles.statValue}>Live</div>
                <div className={styles.statLabel}>Jobs updated daily</div>
              </div>
              <div className={styles.statArrow}>→</div>
            </div>

            <div className={styles.statCard} onClick={() => navigate("/credits")} style={{ cursor: "pointer" }}>
              <div className={styles.statIcon}><CreditCard size={22} strokeWidth={2} /></div>
              <div className={styles.statBody}>
                <div className={styles.statValue}>{credits}</div>
                <div className={styles.statLabel}>Credits available</div>
              </div>
              <div className={styles.statArrow}>+</div>
            </div>

            <div className={styles.statCard}>
              <div className={styles.statIcon}><Target size={22} strokeWidth={2} /></div>
              <div className={styles.statBody}>
                <div className={styles.statValue}>{userProfile?.skills?.length || 0}</div>
                <div className={styles.statLabel}>Skills on profile</div>
              </div>
              <div className={styles.statArrow} onClick={() => navigate("/profile")} style={{ cursor: "pointer" }}>+</div>
            </div>
          </div>

          {/* ── Two-column: Quick actions + Recent gigs ── */}
          <div className={styles.twoCol}>
            {/* Quick actions */}
            <div className={styles.panel}>
              <div className={styles.panelHeader}>
                <h2 className={styles.panelTitle}>Quick Actions</h2>
              </div>
              <div className={styles.actionList}>
                <button className={styles.actionItem} onClick={() => navigate("/jobs")}>
                  <div className={styles.actionIconWrap} style={{ background: "rgba(245,166,35,0.12)", color: "var(--color-accent)" }}>
                    <Search size={17} strokeWidth={2} />
                  </div>
                  <div className={styles.actionBody}>
                    <div className={styles.actionTitle}>Browse All Jobs</div>
                    <div className={styles.actionDesc}>Thousands of jobs across South Africa</div>
                  </div>
                  <span className={styles.actionChev}>›</span>
                </button>

                <button className={styles.actionItem} onClick={() => navigate("/jobs?mode=freelance")}>
                  <div className={styles.actionIconWrap} style={{ background: "rgba(0,200,150,0.1)", color: "#00C896" }}>
                    <Laptop size={17} strokeWidth={2} />
                  </div>
                  <div className={styles.actionBody}>
                    <div className={styles.actionTitle}>Freelance Gigs</div>
                    <div className={styles.actionDesc}>Flexible work and contract opportunities</div>
                  </div>
                  <span className={styles.actionChev}>›</span>
                </button>

                <button className={styles.actionItem} onClick={() => navigate("/jobs?mode=permanent")}>
                  <div className={styles.actionIconWrap} style={{ background: "rgba(100,120,255,0.1)", color: "#7B8FFF" }}>
                    <Building2 size={17} strokeWidth={2} />
                  </div>
                  <div className={styles.actionBody}>
                    <div className={styles.actionTitle}>Permanent Roles</div>
                    <div className={styles.actionDesc}>Full-time positions across South Africa</div>
                  </div>
                  <span className={styles.actionChev}>›</span>
                </button>

                <button className={styles.actionItem} onClick={() => navigate("/credits")}>
                  <div className={styles.actionIconWrap} style={{ background: "rgba(245,166,35,0.15)", color: "var(--color-accent)" }}>
                    <CreditCard size={17} strokeWidth={2} />
                  </div>
                  <div className={styles.actionBody}>
                    <div className={styles.actionTitle}>Buy Credits</div>
                    <div className={styles.actionDesc}>Unlock local gig contact details</div>
                  </div>
                  <span className={styles.actionChev}>›</span>
                </button>
              </div>
            </div>

            {/* Recent local gigs */}
            <div className={styles.panel}>
              <div className={styles.panelHeader}>
                <h2 className={styles.panelTitle}>Recent Local Gigs</h2>
                <button className={styles.panelLink} onClick={() => navigate("/jobs")}>View all →</button>
              </div>

              {loadingJobs ? (
                <div className={styles.recentLoading}>
                  {[...Array(4)].map((_, i) => <div key={i} className={styles.recentSkeleton} />)}
                </div>
              ) : recentJobs.length === 0 ? (
                <div className={styles.recentEmpty}>
                  <p>No local gigs yet.</p>
                  <p style={{ fontSize: 13, marginTop: 8 }}>
                    Run the scraper: <code>cd scraper && node run.js</code>
                  </p>
                </div>
              ) : (
                <div className={styles.recentList}>
                  {recentJobs.map(job => (
                    <div key={job.id} className={styles.recentItem}>
                      <div className={styles.recentItemLeft}>
                        <div className={styles.recentTitle}>{job.title}</div>
                        <div className={styles.recentMeta}>{job.location}</div>
                      </div>
                      <div className={styles.recentRight}>
                        <div className={styles.recentSource}>GigScout</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Skills snapshot ── */}
          {userProfile?.skills?.length > 0 && (
            <div className={styles.skillsPanel}>
              <div className={styles.panelHeader}>
                <h2 className={styles.panelTitle}>Your Skills</h2>
                <button className={styles.panelLink} onClick={() => navigate("/profile")}>Edit →</button>
              </div>
              <div className={styles.skillTags}>
                {userProfile.skills.map(skill => (
                  <button key={skill} className={styles.skillTag}
                    onClick={() => navigate(`/jobs?q=${encodeURIComponent(skill)}`)}>
                    {skill}
                  </button>
                ))}
                <button className={styles.skillTagAdd} onClick={() => navigate("/profile")}>
                  + Add skill
                </button>
              </div>
            </div>
          )}
        </main>
      </div>
    </AppLayout>
  );
}
